import { faClose, faSackDollar } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import type { TimeClockLaborCost } from "../../../Objects/TimeClock/TimeClockLaborCost"
import { formatWorkedMinutes } from "../../../Objects/TimeClock/TimeClockSettings"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface TimeClockLaborCostPopupProps {
  laborCost: TimeClockLaborCost[]
  onClose: () => void
}

export const TimeClockLaborCostPopup: React.FC<TimeClockLaborCostPopupProps> = ({ laborCost, onClose }) => {
  const { t } = useTranslation(["timeClock", "helpPopup"])

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("management.labor_cost")}
              icon={faSackDollar}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                  <PopupHelpButton
                    variant="global"
                    helpKey="pages.time-clock-labor-cost"
                    title={t("helpPopup:global.timeClockLaborCost.title")}
                    description={t("helpPopup:global.timeClockLaborCost.description")}
                    bullets={t("helpPopup:global.timeClockLaborCost.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.timeClock.timeClockLaborCostPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
              <div className="flex max-h-[72vh] flex-col gap-2 overflow-y-auto p-2">
                <p className="text-xs font-medium text-gray-600">{t("management.labor_cost_disclaimer")}</p>

                {laborCost.length === 0 && (
                  <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-center text-sm font-medium text-slate-500">
                    {t("management.no_entries")}
                  </p>
                )}

                {laborCost.map((cost) => (
                  <div
                    key={cost.employeeId}
                    className="flex flex-col gap-2 rounded-2xl border-2 border-gray-200 bg-gradient-to-r from-orange-50 to-white p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-extrabold text-gray-900">{cost.employeeName}</span>

                        <span className="truncate text-xs font-medium text-gray-600">
                          {cost.usesRegisteredSchedule
                            ? t("management.labor_cost_registered_base")
                            : t("management.labor_cost_default_base")}
                        </span>
                      </div>

                      <span className="flex shrink-0 flex-col items-end">
                        <span className="text-[11px] font-semibold text-gray-600">
                          {t("management.estimated_cost")}
                        </span>

                        <span className="whitespace-nowrap text-xl font-extrabold text-orange-700">
                          {convertToLocalMoney(cost.estimatedCost)}
                        </span>
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-2 border-t border-gray-200 pt-2">
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
                        {t("management.worked")}: {formatWorkedMinutes(cost.workedMinutes)}
                      </span>

                      <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                        {t("management.total_overtime")}: {formatWorkedMinutes(cost.overtimeMinutes)}
                      </span>

                      <span className="rounded-full bg-cyan-100 px-2 py-1 text-xs font-semibold text-cyan-700">
                        {t("management.hourly_rate")}: {convertToLocalMoney(cost.hourlyRate)}
                      </span>

                      <span className="rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700">
                        {t("management.monthly_hours_base")}: {cost.monthlyHoursBase}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
